import * as stylex from "@stylexjs/stylex";
import { ListFilter } from "lucide-react";
import { useState } from "react";
import type { Grouping } from "@aco/core";
import type { Platform } from "@aco/core";
import type { PlatformHealth } from "@aco/core";
import type { ReactNode } from "react";
import { resolveAssignmentColumns } from "@/components/analytics/EntityBreakdown";
import ReportingDateChip from "@/components/ReportingDateChip";
import { ChipPanel, PanelColumn, PanelListSection, PanelSegments } from "@/components/ReportingPanel";
import { ChipRow } from "@/components/ui/Chip";
import "@/design/tokens.stylex";
import { colors, space, text } from "@/design/tokens.stylex";
import type { ChangeDisplay, DateChange, FilterChange } from "@/lib/reporting-apply";
import type { ActiveBreakdown } from "@/lib/reporting-breakdowns";
import { latestSelectableReportingDay } from "@/lib/utils";
import { describeMissingDays, isUnhealthy } from "@/lib/platform-health";
export type { ChangeDisplay } from "@/lib/reporting-apply";
const PLATFORM_NAMES: Record<Platform, string> = {
  meta: "Meta",
  google: "Google",
  tiktok: "TikTok",
  snapchat: "Snapchat"
};

/** By campaign / By creative: the active Breakdowns whose results can narrow the rows. */
interface AssignmentFilters {
  breakdowns: ActiveBreakdown[];
  scope: string;
  filters: Record<string, string[]>;
}
interface ReportingToolbarProps {
  dateRange: {
    start: string;
    end: string;
  };
  preset: string;
  grouping: Grouping;
  platform: "all" | Platform;
  availablePlatforms: Platform[];
  freshness: PlatformHealth[];
  showGrouping?: boolean;
  showPlatform?: boolean;
  showChange?: boolean;
  segment?: string;
  segments?: Array<{
    id: string;
    name: string;
  }>;
  breakdown?: string[];
  breakdowns?: Array<{
    id: string;
    name: string;
  }>;
  assignments?: AssignmentFilters;
  changeDisplay: ChangeDisplay;
  /** Applies the Date panel's staged range, grouping and comparison at once. */
  onDateApply: (change: DateChange) => void;
  /** Applies the Filter panel's staged settings at once; the chip's × uses it too. */
  onFilterApply: (change: FilterChange) => void;
  /** More chips after Filter, e.g. the campaign a creative list is narrowed to. */
  filterControls?: ReactNode;
  /** Quiet view controls at the row's end, e.g. the creative layout toggle. */
  trailingControls?: ReactNode;
}
export default function ReportingToolbar({
  dateRange,
  preset,
  grouping,
  platform,
  availablePlatforms,
  freshness,
  showGrouping = true,
  showPlatform = true,
  showChange = true,
  segment,
  segments = [],
  breakdown,
  breakdowns = [],
  assignments,
  changeDisplay,
  onDateApply,
  onFilterApply,
  filterControls,
  trailingControls
}: ReportingToolbarProps) {
  const unhealthy = freshness.filter(isUnhealthy);

  // Mercury's chip row: the Date chip (range, grouping, comparison), then one
  // Filter chip holding everything that narrows or splits the report, with
  // freshness and quiet view controls at the end.
  return <ChipRow end={unhealthy.length || trailingControls ? <>
            {unhealthy.length > 0 && <span className="x3nfvp2 x6s0dn4 x5vihii x3fqqzs xnclacp x7x6yu3 xuxw1ft">
                {unhealthy.map(item => <span key={item.platform}>
                    <span className="xn80e1m">{item.platform}</span>{" "}
                    {item.stale || item.missingInRange ? item.latestDate ? `${daysBehind(item.latestDate, dateRange.end)} d behind` : "no data" : describeMissingDays(item)}
                  </span>)}
              </span>}
            {trailingControls}
          </> : undefined}>
      <ReportingDateChip dateRange={dateRange} preset={preset} grouping={grouping} changeDisplay={changeDisplay} showGrouping={showGrouping} showChange={showChange} onApply={onDateApply} />
      <FilterChip platform={platform} platforms={showPlatform && availablePlatforms.length > 1 ? availablePlatforms : []} segment={segment} segments={segments} breakdown={breakdown ?? []} breakdowns={breakdowns} assignments={assignments} onApply={onFilterApply} />
      {filterControls}
    </ChipRow>;
}
interface FilterDraft {
  segment: string;
  platform: "all" | Platform;
  breakdown: string[];
  assignments: Record<string, string[]>;
}
const sameSet = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every(value => b.includes(value));

/**
 * One chip for what narrows or splits the report: the saved Filter, Platform,
 * Split by (Breakdowns) and, By campaign or By creative, the Breakdown results.
 * It reads "Filter" until something is set, then what is set. Edits wait for
 * Apply; the chip's × clears everything at once.
 */
function FilterChip({
  platform,
  platforms,
  segment,
  segments,
  breakdown,
  breakdowns,
  assignments,
  onApply
}: {
  platform: "all" | Platform;
  platforms: Platform[];
  segment?: string;
  segments: Array<{
    id: string;
    name: string;
  }>;
  breakdown: string[];
  breakdowns: Array<{
    id: string;
    name: string;
  }>;
  assignments?: AssignmentFilters;
  onApply: (change: FilterChange) => void;
}) {
  const [open, setOpen] = useState(false);
  const applied: FilterDraft = {
    segment: segment ?? "all",
    platform,
    breakdown,
    assignments: assignments?.filters ?? {}
  };
  const [draft, setDraft] = useState(applied);
  const appliedColumns = assignments?.breakdowns.length ? resolveAssignmentColumns(assignments.breakdowns, applied.assignments, assignments.scope) : [];
  if (!segments.length && !platforms.length && !breakdowns.length && !appliedColumns.length) return null;
  // Results can narrow the rows only for Breakdowns that are split by and loaded.
  const draftColumns = assignments ? resolveAssignmentColumns(assignments.breakdowns.filter(item => draft.breakdown.includes(item.id)), draft.assignments, assignments.scope) : [];
  const segmentName = segment ? segments.find(item => item.id === segment)?.name : undefined;
  const splitNames = breakdowns.filter(item => breakdown.includes(item.id)).map(item => item.name).join(", ");
  const resultNames = appliedColumns.flatMap(column => column.options.filter(option => option.value && column.selected.has(option.value)).map(option => option.label)).join(", ");
  const summary = [segmentName, platform === "all" ? undefined : PLATFORM_NAMES[platform], splitNames ? `Split by ${splitNames}` : undefined, resultNames || undefined].filter((part): part is string => Boolean(part));
  const change = (from: FilterDraft, to: FilterDraft): FilterChange => ({
    ...(to.segment !== from.segment && {
      segment: to.segment
    }),
    ...(to.platform !== from.platform && {
      platform: to.platform
    }),
    ...(!sameSet(to.breakdown, from.breakdown) && {
      breakdown: [...to.breakdown].sort()
    }),
    ...(assignments && JSON.stringify(to.assignments) !== JSON.stringify(from.assignments) && {
      assignments: to.assignments
    })
  });
  const staged = change(applied, draft);
  function openChange(next: boolean) {
    // Opening starts from what the report shows; closing drops staged edits.
    if (next) setDraft(applied);
    setOpen(next);
  }
  return <ChipPanel ariaLabel={summary.length ? `Filter: ${summary.join(" · ")}` : "Filter"} panelLabel="Filter" icon={<ListFilter size={14} />} label={summary.length ? summary : "Filter"} onClear={summary.length ? () => onApply(change(applied, {
    segment: "all",
    platform: "all",
    breakdown: [],
    assignments: Object.fromEntries(Object.keys(applied.assignments).map(key => [key, []]))
  })) : undefined} clearLabel="Clear filters" open={open} onOpenChange={openChange} dirty={Object.keys(staged).length > 0} onApply={() => {
    onApply(staged);
    setOpen(false);
  }}>
      <PanelColumn>
        {segments.length > 0 && <PanelListSection label="Filter" name="Filter" options={[{
        value: "all",
        label: "All data"
      }, ...segments.map(item => ({
        value: item.id,
        label: item.name
      }))]} selected={[draft.segment]} onSelect={value => setDraft(current => ({
        ...current,
        segment: value
      }))} />}
        {platforms.length > 0 && <PanelSegments label="Platform" options={[{
        value: "all" as const,
        label: "All"
      }, ...platforms.map(value => ({
        value,
        label: PLATFORM_NAMES[value]
      }))]} value={draft.platform} onChange={value => setDraft(current => ({
        ...current,
        platform: value
      }))} />}
        {breakdowns.length > 0 && <PanelListSection label="Split by" name="Split by" multiple options={breakdowns.map(item => ({
        value: item.id,
        label: item.name
      }))} selected={draft.breakdown} onSelect={value => setDraft(current => ({
        ...current,
        breakdown: current.breakdown.includes(value) ? current.breakdown.filter(item => item !== value) : [...current.breakdown, value]
      }))} />}
        {draftColumns.map(column => <PanelListSection key={column.key} label={column.name} name={column.name} multiple options={column.options.filter(option => option.value)} selected={column.values} onSelect={id => setDraft(current => ({
        ...current,
        assignments: {
          ...current.assignments,
          [column.key]: column.selected.has(id) ? column.values.filter(value => value !== id) : [...column.values, id]
        }
      }))} />)}
      </PanelColumn>
    </ChipPanel>;
}
function daysBehind(latest: string, end: string) {
  const expectedThrough = [end, latestSelectableReportingDay()].sort()[0]!;
  return Math.max(0, Math.round((new Date(`${expectedThrough}T00:00:00Z`).getTime() - new Date(`${latest}T00:00:00Z`).getTime()) / 86_400_000));
}